// Buchhaltung & Kasse — nur für Admins.
//
// Oben die Lage in vier Zahlen. Darunter zwei Register: Rechnungen und
// Lieferscheine. Wer Geld sucht, will nicht filtern lernen — Offenes steht
// standardmäßig oben. Der Plus-Knopf stellt einen Beleg von Hand aus.

import { useMemo, useState } from "react";
import {
  useKassenLage,
  useRechnungen,
  useAlsBezahltBuchen,
  useZahlungZuruecknehmen,
  istUeberfaellig,
  type Rechnung,
  type RechnungsFilter,
} from "@/lib/buchhaltung";
import { useLieferscheine, istHandbeleg, type BelegTyp, type Lieferschein } from "@/lib/belege";
import { BelegEditor } from "./BelegEditor";
import { LogoLoader } from "@/components/LogoLoader";
import { Button, Card, EmptyState, Segmented, Sheet, cn, euro } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";

type Register = "rechnungen" | "lieferscheine";

export default function AdminKassePage() {
  const { data: lage, isLoading: lageLaedt } = useKassenLage();
  const [register, setRegister] = useState<Register>("rechnungen");
  const [editor, setEditor] = useState<BelegTyp | null>(null);

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader titel="Buchhaltung & Kasse" />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-24">
        {/* Die Lage in vier Zahlen */}
        <div className="mb-5 grid grid-cols-2 gap-3">
          <Kennzahl
            label="Offen"
            wert={lageLaedt ? "…" : euro(lage?.offenCents ?? 0)}
            zusatz={lageLaedt ? undefined : `${lage?.offeneRechnungen ?? 0} Rechnungen`}
          />
          <Kennzahl
            label="Überfällig"
            wert={lageLaedt ? "…" : euro(lage?.ueberfaelligCents ?? 0)}
            zusatz={lageLaedt ? undefined : `${lage?.ueberfaellig ?? 0} Rechnungen`}
            warnung={(lage?.ueberfaellig ?? 0) > 0}
          />
          <Kennzahl
            label="Bar beim Fahrer"
            wert={lageLaedt ? "…" : euro(lage?.barImUmlauf ?? 0)}
            zusatz="noch nicht übergeben"
          />
          <Kennzahl
            label="Umsatz diesen Monat"
            wert={lageLaedt ? "…" : euro(lage?.umsatzMonatCents ?? 0)}
          />
        </div>

        <Segmented
          className="mb-4"
          value={register}
          onChange={setRegister}
          options={[
            { value: "rechnungen", label: "Rechnungen" },
            { value: "lieferscheine", label: "Lieferscheine" },
          ]}
        />

        {register === "rechnungen" ? <RechnungsListe /> : <LieferscheinListe />}
      </div>

      {/* Beleg von Hand ausstellen */}
      <button
        onClick={() => setEditor(register === "rechnungen" ? "rechnung" : "lieferschein")}
        aria-label={register === "rechnungen" ? "Rechnung erstellen" : "Lieferschein erstellen"}
        className={cn(
          "fixed bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-5 z-30",
          "flex h-14 w-14 items-center justify-center rounded-full",
          // Die Palette kennt NUR gruen-500 / gruen-400 / gruen-on. Ein
          // `bg-gruen-600` erzeugt keine Klasse — der Knopf bliebe farblos und
          // wäre im hellen Theme praktisch unsichtbar. Genau das ist passiert.
          "bg-gruen-500 text-gruen-on shadow-lg shadow-black/20",
          "transition-transform duration-fast active:scale-95",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-400 focus-visible:ring-offset-2"
        )}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M12 5v14M5 12h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {editor && <BelegEditor startTyp={editor} onClose={() => setEditor(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════ Rechnungen

const RECHNUNGS_FILTER: { wert: RechnungsFilter; label: string }[] = [
  { wert: "offen", label: "Offen" },
  { wert: "ueberfaellig", label: "Überfällig" },
  { wert: "bezahlt", label: "Bezahlt" },
  { wert: "alle", label: "Alle" },
];

function RechnungsListe() {
  const [filter, setFilter] = useState<RechnungsFilter>("offen");
  const { data: rechnungen = [], isLoading } = useRechnungen(filter);
  const [offen, setOffen] = useState<Rechnung | null>(null);

  return (
    <>
      <FilterLeiste
        optionen={RECHNUNGS_FILTER}
        aktiv={filter}
        onWaehlen={(w) => setFilter(w as RechnungsFilter)}
      />

      {isLoading ? (
        <Laedt was="Rechnungen" />
      ) : rechnungen.length === 0 ? (
        <EmptyState
          title="Nichts gefunden"
          hint={
            filter === "offen"
              ? "Alle Rechnungen sind bezahlt."
              : filter === "ueberfaellig"
                ? "Nichts ist überfällig."
                : "Es gibt noch keine Rechnungen."
          }
        />
      ) : (
        <ul className="space-y-2">
          {rechnungen.map((r) => (
            <li key={r.id}>
              <RechnungsZeile r={r} onOeffnen={() => setOffen(r)} />
            </li>
          ))}
        </ul>
      )}

      {offen && <RechnungSheet r={offen} onClose={() => setOffen(null)} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════ Lieferscheine

type SlipFilter = "alle" | "tour" | "hand";

const SLIP_FILTER: { wert: SlipFilter; label: string }[] = [
  { wert: "alle", label: "Alle" },
  { wert: "tour", label: "Aus Tour" },
  { wert: "hand", label: "Von Hand" },
];

function LieferscheinListe() {
  const [filter, setFilter] = useState<SlipFilter>("alle");
  const { data: alle = [], isLoading } = useLieferscheine();

  // Ein Lieferschein kennt kein "bezahlt" — er belegt eine Übergabe, keine
  // Zahlung. Gefiltert wird deshalb danach, WOHER er kommt.
  const liste = useMemo(() => {
    if (filter === "tour") return alle.filter((s) => !istHandbeleg(s));
    if (filter === "hand") return alle.filter((s) => istHandbeleg(s));
    return alle;
  }, [alle, filter]);

  return (
    <>
      <FilterLeiste optionen={SLIP_FILTER} aktiv={filter} onWaehlen={(w) => setFilter(w as SlipFilter)} />

      {isLoading ? (
        <Laedt was="Lieferscheine" />
      ) : liste.length === 0 ? (
        <EmptyState
          title="Keine Lieferscheine"
          hint={
            filter === "hand"
              ? "Von Hand ausgestellte Lieferscheine erscheinen hier."
              : "Lieferscheine entstehen automatisch, sobald der Fahrer einen Stop abschließt."
          }
        />
      ) : (
        <ul className="space-y-2">
          {liste.map((s) => (
            <li key={s.id}>
              <LieferscheinZeile s={s} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function LieferscheinZeile({ s }: { s: Lieferschein }) {
  const inhalt = (
    <Card className="flex items-center justify-between gap-3 px-4 py-3 transition-colors duration-fast hover:border-ink-600">
      <div className="min-w-0">
        <div className="truncate text-[15px] font-medium text-ink-100">
          {s.customer?.display_name ?? "Unbekannt"}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-sm text-ink-400">
          <span className="font-mono text-xs">{s.slip_number ?? "—"}</span>
          <span className="tabular-nums">{datum(s.delivered_at)}</span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {istHandbeleg(s) ? (
          <span className="rounded-full border border-ink-700 px-2 py-0.5 text-xs text-ink-400">
            von Hand
          </span>
        ) : (
          <span className="rounded-full border border-gruen-500/30 bg-gruen-500/10 px-2 py-0.5 text-xs text-gruen-400">
            Tour
          </span>
        )}
        {s.pdf_url && <span className="text-sm text-ink-500">PDF ↗</span>}
      </div>
    </Card>
  );

  return s.pdf_url ? (
    <a href={s.pdf_url} target="_blank" rel="noopener noreferrer" className="block">
      {inhalt}
    </a>
  ) : (
    inhalt
  );
}

// ═══════════════════════════════════════════════════════ Bausteine

function FilterLeiste<T extends string>({
  optionen,
  aktiv,
  onWaehlen,
}: {
  optionen: { wert: T; label: string }[];
  aktiv: T;
  onWaehlen: (w: T) => void;
}) {
  return (
    <div className="-mx-4 mb-4 flex gap-1.5 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {optionen.map((f) => (
        <button
          key={f.wert}
          onClick={() => onWaehlen(f.wert)}
          aria-pressed={aktiv === f.wert}
          className={cn(
            "flex h-9 shrink-0 items-center rounded-full border px-4 text-sm font-medium",
            "transition-colors duration-fast",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gruen-500/60",
            aktiv === f.wert
              ? "border-gruen-500/50 bg-gruen-500/15 text-gruen-400"
              : "border-ink-700 bg-ink-800/60 text-ink-300 hover:border-ink-600 hover:text-ink-100"
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function Laedt({ was }: { was: string }) {
  return (
    <div className="flex justify-center py-16">
      <LogoLoader size="md" label={`${was} werden geladen`} />
    </div>
  );
}

function Kennzahl({
  label,
  wert,
  zusatz,
  warnung,
}: {
  label: string;
  wert: string;
  zusatz?: string;
  warnung?: boolean;
}) {
  return (
    <Card className={cn("px-4 py-3", warnung && "border-warn-bg/40 bg-warn-bg/5")}>
      <div className="text-xs uppercase tracking-wider text-ink-400">{label}</div>
      <div
        className={cn(
          "mt-1 text-xl font-semibold tabular-nums",
          warnung ? "text-warn-fg" : "text-ink-50"
        )}
      >
        {wert}
      </div>
      {zusatz && <div className="mt-0.5 text-xs tabular-nums text-ink-500">{zusatz}</div>}
    </Card>
  );
}

function RechnungsZeile({ r, onOeffnen }: { r: Rechnung; onOeffnen: () => void }) {
  const bezahlt = r.status === "paid";
  const spaet = istUeberfaellig(r);

  return (
    <button onClick={onOeffnen} className="w-full text-left">
      <Card
        className={cn(
          "flex items-center justify-between gap-3 px-4 py-3 transition-colors duration-fast hover:border-ink-600",
          spaet && "border-danger-bg/30"
        )}
      >
        <div className="min-w-0">
          <div className="truncate text-[15px] font-medium text-ink-100">
            {r.customer?.display_name ?? "Unbekannt"}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-ink-400">
            <span className="font-mono text-xs">{r.invoice_number}</span>
            <span className="tabular-nums">{datum(r.period_start)}</span>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-[15px] font-semibold tabular-nums text-ink-50">
            {euro(r.total_cents)}
          </div>
          <div className="mt-0.5">
            {r.status === "cancelled" ? (
              <span className="text-xs text-ink-500">storniert</span>
            ) : bezahlt ? (
              <span className="text-xs font-medium text-gruen-400">bezahlt</span>
            ) : spaet ? (
              <span className="text-xs font-medium text-danger-fg">
                überfällig seit {datum(r.due_at!)}
              </span>
            ) : (
              <span className="text-xs text-ink-400">
                fällig {r.due_at ? datum(r.due_at) : "—"}
              </span>
            )}
          </div>
        </div>
      </Card>
    </button>
  );
}

function RechnungSheet({ r, onClose }: { r: Rechnung; onClose: () => void }) {
  const buchen = useAlsBezahltBuchen();
  const zurueck = useZahlungZuruecknehmen();
  const [fehler, setFehler] = useState<string | null>(null);
  const bezahlt = r.status === "paid";

  return (
    <Sheet
      open
      onClose={onClose}
      title={r.invoice_number ?? "Rechnung"}
      footer={
        <div className="space-y-2">
          {fehler && (
            <p className="rounded-lg border border-danger-bg/30 bg-danger-bg/10 px-3 py-2 text-sm text-danger-fg">
              {fehler}
            </p>
          )}

          {bezahlt ? (
            <Button
              className="w-full"
              loading={zurueck.isPending}
              onClick={async () => {
                setFehler(null);
                try {
                  await zurueck.mutateAsync(r.id);
                  onClose();
                } catch (e) {
                  setFehler((e as Error).message);
                }
              }}
            >
              Zahlung zurücknehmen
            </Button>
          ) : (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              loading={buchen.isPending}
              onClick={async () => {
                setFehler(null);
                try {
                  await buchen.mutateAsync({ id: r.id, total_cents: r.total_cents });
                  onClose();
                } catch (e) {
                  setFehler((e as Error).message);
                }
              }}
            >
              {euro(r.total_cents)} als bezahlt buchen
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <Card className={cn("p-4", bezahlt ? "border-gruen-500/30 bg-gruen-500/5" : undefined)}>
          <div className="text-xs uppercase tracking-wider text-ink-400">Betrag</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-ink-50">
            {euro(r.total_cents)}
          </div>
          <div className="mt-1 text-sm text-ink-400">
            {bezahlt ? (
              <span className="text-gruen-400">
                bezahlt{r.paid_at ? ` am ${datum(r.paid_at)}` : ""}
              </span>
            ) : istUeberfaellig(r) ? (
              <span className="text-danger-fg">überfällig seit {datum(r.due_at!)}</span>
            ) : (
              <>fällig am {r.due_at ? datum(r.due_at) : "—"}</>
            )}
          </div>
        </Card>

        <dl className="space-y-2 text-[15px]">
          <Zeile label="Kunde" wert={r.customer?.display_name ?? "—"} />
          <Zeile label="Kundennummer" wert={r.customer?.customer_number ?? "—"} />
          <Zeile label="Lieferung" wert={datum(r.period_start)} />
          <Zeile label="Bereits bezahlt" wert={euro(r.paid_cents)} />
        </dl>

        {r.pdf_url && (
          <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="block">
            <Button className="w-full">PDF öffnen</Button>
          </a>
        )}

        {!bezahlt && (
          <p className="text-sm text-ink-500">
            Buchen, wenn das Geld eingegangen ist — bar nachgereicht oder überwiesen.
          </p>
        )}
      </div>
    </Sheet>
  );
}

function Zeile({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-400">{label}</dt>
      <dd className="truncate font-medium text-ink-100">{wert}</dd>
    </div>
  );
}

function datum(iso: string) {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}
